(() => {
  const component = () => class extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.abort = new AbortController();
      const listen = (target, event, callback) => target?.addEventListener(event, callback, { signal: this.abort.signal });
      const home = this.classList.contains('mhb');
      const prefix = home ? 'mhb' : 'mfs';
      this.track = this.querySelector(`.${prefix}__track`);
      this.slides = [...this.querySelectorAll(`.${prefix}__slide`)];
      this.controls = this.querySelector(`.${prefix}__controls`);
      this.counter = this.querySelector(`[data-${prefix}-count]`);
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = false;
      this.hovered = this.matches(':hover');
      this.focused = this.contains(document.activeElement);
      this.visible = false;
      this.dragging = false;
      this.editing = false;
      if (!this.track || !this.slides.length) return;
      if (this.controls) this.controls.hidden = this.slides.length < 2;
      const update = () => {
        this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth)));
        if (this.counter) {
          this.counter.textContent = home ? `${this.index + 1} / ${this.slides.length}` : String(this.index + 1);
          this.counter.closest('[aria-live]')?.setAttribute('aria-live', this.paused ? 'polite' : 'off');
        }
        this.slides.forEach((slide, index) => { slide.inert = index !== this.index; });
        this.dataset.activeSlide = String(this.index + 1);
      };
      this.go = (index, instant = false) => {
        const target = (index + this.slides.length) % this.slides.length;
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: instant || this.motion.matches ? 'instant' : 'smooth' });
        this.schedule();
      };
      listen(this.querySelector(`[data-${prefix}-prev]`), 'click', () => this.go(this.index - 1));
      listen(this.querySelector(`[data-${prefix}-next]`), 'click', () => this.go(this.index + 1));
      listen(this.track, 'scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => { update(); this.schedule(); });
      });
      listen(this, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      listen(this.pauseButton, 'click', () => {
        this.paused = !this.paused;
        this.pauseButton.textContent = this.paused ? this.pauseButton.dataset.play : this.pauseButton.dataset.pause;
        this.pauseButton.setAttribute('aria-label', this.pauseButton.textContent);
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        update(); this.schedule();
      });
      listen(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
      listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => { this.focused = true; this.schedule(); });
      listen(this, 'focusout', event => { this.focused = this.contains(event.relatedTarget); this.schedule(); });
      listen(this.track, 'pointerdown', () => { this.dragging = true; this.schedule(); });
      listen(window, 'pointerup', () => { this.dragging = false; this.schedule(); });
      listen(window, 'pointercancel', () => { this.dragging = false; this.schedule(); });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.motion, 'change', () => this.schedule());
      listen(document, 'shopify:block:select', event => {
        const index = this.slides.indexOf(event.target.closest(`.${prefix}__slide`));
        if (index < 0) return;
        this.editing = true; this.schedule(); this.go(index, true);
      });
      listen(document, 'shopify:block:deselect', () => { this.editing = false; this.schedule(); });
      const fit = () => {
        const top = this.getBoundingClientRect().top + scrollY;
        const controls = this.controls?.getBoundingClientRect().height || 0;
        const height = Math.max(280, Math.min(640, innerHeight - top - controls - 128));
        this.style.setProperty('--magpie-hero-height', `${Math.round(height)}px`);
      };
      listen(window, 'resize', () => { fit(); this.go(this.index, true); });
      this.headerObserver = new ResizeObserver(fit);
      const header = document.querySelector('.section-header');
      if (header) this.headerObserver.observe(header);
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: .15 });
      this.visibilityObserver.observe(this);
      update(); fit();
      document.fonts?.ready.then(() => { if (this.isConnected) fit(); });
    }
    schedule() {
      clearTimeout(this.timer);
      const enabled = this.dataset.autoplay !== 'false' && this.slides?.length > 1;
      const playing = enabled && !this.paused && !this.hovered && !this.focused && !this.dragging && !this.editing && this.visible && !document.hidden && !this.motion.matches;
      this.dataset.playing = String(playing);
      if (playing) this.timer = setTimeout(() => this.go(this.index + 1), Number(this.dataset.interval) || 3000);
    }
    cleanup() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.visibilityObserver?.disconnect();
      this.headerObserver?.disconnect();
    }
    disconnectedCallback() { this.cleanup(); }
  };
  for (const name of ['magpie-fashion-spotlight', 'magpie-home-banner']) {
    if (!customElements.get(name)) customElements.define(name, component());
  }
})();
